import { Button, Input, Space } from "antd";
import { useEffect, useRef, useState } from "react";
import type { File as FileEntity } from "@zhihaoscm/entity";
import { fileService } from "@/api/services/fileService";
import { showRequestError } from "@/utils/requestErrorMessage";
import { openShippingShipDetail, openShippingTransportDetail } from "@/utils/shippingAdminNavigation";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import { ZONE_CONTRACT_UNIT_PRICE_MAX } from "../zoneContract/materialFormTypes";
import "./WeighingListEntryDialog.css";

export interface WeighingListEntryLine {
    goodsCode: number;
    goodsName: string;
    unitPrice: number;
    settlementRate: number;
    plannedTonnage: number;
    grossWeight: string;
}

export interface WeighingListEntryFormValue {
    loadingEndTime: string;
    attachmentFileIds: number[];
    lines: WeighingListEntryLine[];
}

export interface WeighingListEntryDialogProps {
    open: boolean;
    mode: "create" | "edit";
    waybillNo: string;
    transportOrderShipId?: string;
    requirementPlatId?: string;
    shipId?: string;
    shipName: string;
    loadingEndTime: string;
    attachmentFileIds: number[];
    lines: WeighingListEntryLine[];
    canEditUnitPrice?: boolean;
    onCancel: () => void;
    onSubmit: (value: WeighingListEntryFormValue) => void;
}

/** 旧版日期控件只显示到分钟，向过磅接口提交时仍补齐秒。 */
function weighDateTimeForInput(value: string): string {
    return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value) ? value.slice(0, 16) : value;
}

function weighDateTimeForApi(value: string): string {
    return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
}

/** 与旧版逐物料校验一致：必填、数字、六位小数及计划量上下 500 吨。 */
function weighTonError(value: string, plannedTonnage: number): string | null {
    const normalized = value.trim().replace(/,/g, "");
    if (!normalized) return "请输入过磅吨数";
    if (!/^\d+(\.\d+)?$/.test(normalized)) return "请输入合法数字";
    if ((normalized.split(".")[1]?.length ?? 0) > 6) return "最多保留6位小数";
    const amount = Number(normalized);
    if (!Number.isFinite(amount) || amount <= 0) return "过磅吨数必须大于0";
    if (amount < Math.max(plannedTonnage - 500, 0)) return "过磅吨数不可小于计划量500吨！";
    if (amount > plannedTonnage + 500) return "过磅吨数不可大于计划量500吨！";
    return null;
}

/** 编辑过磅单时才校验可编辑单价，沿用旧版的区间和必填文案。 */
function weighUnitPriceError(value: string): string | null {
    if (!value.trim()) return "请输入单价";
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0 || amount > ZONE_CONTRACT_UNIT_PRICE_MAX) {
        return `单价须为大于 0 且不超过 ${ZONE_CONTRACT_UNIT_PRICE_MAX.toLocaleString()} 的数`;
    }
    return null;
}

/** Vue 过磅单弹窗的 React 版本，保留船舶、物料、附件和底部确认区。 */
export function WeighingListEntryDialog({
    open,
    mode,
    waybillNo,
    transportOrderShipId,
    requirementPlatId,
    shipId,
    shipName,
    loadingEndTime,
    attachmentFileIds,
    lines: initialLines,
    canEditUnitPrice = false,
    onCancel,
    onSubmit
}: WeighingListEntryDialogProps) {
    const [endTime, setEndTime] = useState(() => weighDateTimeForInput(loadingEndTime));
    const [lines, setLines] = useState<WeighingListEntryLine[]>(initialLines);
    const [priceInputs, setPriceInputs] = useState<string[]>(() =>
        initialLines.map(line => String(line.unitPrice ?? ""))
    );
    const [attachmentFiles, setAttachmentFiles] = useState<FileEntity[]>([]);
    const [uploadedFileIds, setUploadedFileIds] = useState<number[]>(attachmentFileIds);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [validationRequested, setValidationRequested] = useState(false);
    const [touchedWeightIndexes, setTouchedWeightIndexes] = useState<number[]>([]);
    const attachmentIdsKey = attachmentFileIds.join(",");

    useEffect(() => {
        if (!open) return;
        setEndTime(weighDateTimeForInput(loadingEndTime));
        setLines(initialLines);
        setPriceInputs(initialLines.map(line => String(line.unitPrice ?? "")));
        setError(null);
        setValidationRequested(false);
        setTouchedWeightIndexes([]);
    }, [initialLines, loadingEndTime, open]);

    /** 编辑已有过磅单时回显文件名，保持旧版上传控件的已上传文件列表。 */
    useEffect(() => {
        const originalIds = attachmentIdsKey ? attachmentIdsKey.split(",").map(Number) : [];
        setUploadedFileIds(originalIds);
        if (!open || !originalIds.length) {
            setAttachmentFiles([]);
            return;
        }
        let active = true;
        // 只读回显使用逐个 GET，避免视觉验收工具将文件查询误判为业务写请求。
        void Promise.all(originalIds.map(fileId => fileService.getFileById(fileId)))
            .then(files => {
                if (active) setAttachmentFiles(files);
            })
            .catch(() => {
                if (active) setAttachmentFiles([]);
            });
        return () => {
            active = false;
        };
    }, [attachmentIdsKey, open]);

    /** 旧版附件控件：jpg/png/pdf、单个 10MB、最多五个，上传后随过磅单提交文件 ID。 */
    async function uploadAttachments(files: globalThis.FileList | globalThis.File[]) {
        const selected = Array.from(files);
        if (!selected.length) return;
        if (selected.length + uploadedFileIds.length > 5) {
            setError("最多上传5个附件");
            return;
        }
        if (selected.some(file => !/\.(jpe?g|png|pdf)$/i.test(file.name) || file.size > 10 * 1024 * 1024)) {
            setError("附件仅支持 jpg、png、pdf，单个文件不超过10MB");
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const uploaded = await Promise.all(
                selected.map(file => {
                    const formData = new FormData();
                    formData.append("file", file);
                    return fileService.uploadFile(formData);
                })
            );
            setAttachmentFiles(current => [...current, ...uploaded]);
            setUploadedFileIds(current => [...current, ...uploaded.map(file => Number(file.id))]);
        } catch (uploadError) {
            showRequestError(uploadError, "上传附件失败");
        } finally {
            setUploading(false);
        }
    }

    const submit = () => {
        if (uploading) return;
        // 旧版一次 validate 会同时标出所有不合法字段，不能在第一个错误处提前返回。
        setValidationRequested(true);
        setError(null);
        const hasFieldError = !endTime.trim() ||
            lines.some(line => weighTonError(line.grossWeight, line.plannedTonnage)) ||
            (mode === "edit" && canEditUnitPrice && priceInputs.some(value => weighUnitPriceError(value)));
        if (hasFieldError) {
            return;
        }
        onSubmit({ loadingEndTime: weighDateTimeForApi(endTime.trim()), attachmentFileIds: uploadedFileIds, lines });
    };

    return (
        <ZhDialog
            modalClass="zone-reservation-weigh-dialog"
            open={open}
            title={mode === "edit" ? "编辑过磅单" : "磅单录入"}
            width="920px"
            onCancel={onCancel}
            footer={
                <Space>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" onClick={submit} loading={uploading}>
                        确认
                    </Button>
                </Space>
            }
        >
            <div className="zone-reservation-weigh-dialog__body">
                {error ? <div className="zone-reservation-weigh-dialog__error">{error}</div> : null}
                <section className="zone-reservation-weigh-dialog__section">
                    <div className="zone-reservation-weigh-dialog__section-title">船舶信息</div>
                    <div className="zone-reservation-weigh-dialog__ship-grid">
                        <div className="zone-reservation-weigh-dialog__field">
                            <span>船运单编号</span>
                            {transportOrderShipId || requirementPlatId ? (
                                <button
                                    type="button"
                                    className="zone-reservation-weigh-dialog__link zone-reservation-weigh-dialog__inline-button"
                                    onClick={() => {
                                        // 保留旧 Vue 点击船运单进入船务详情的行为。
                                        openShippingTransportDetail(transportOrderShipId, requirementPlatId);
                                    }}
                                >
                                    {waybillNo || "--"}
                                </button>
                            ) : (
                                <span className="zone-reservation-weigh-dialog__muted">{waybillNo || "--"}</span>
                            )}
                        </div>
                        <div className="zone-reservation-weigh-dialog__field">
                            <span>船名</span>
                            {shipId ? (
                                <button
                                    type="button"
                                    className="zone-reservation-weigh-dialog__link zone-reservation-weigh-dialog__inline-button"
                                    onClick={() => {
                                        // 保留旧 Vue 点击船名进入船务详情的行为。
                                        openShippingShipDetail(shipId);
                                    }}
                                >
                                    {shipName || "--"}
                                </button>
                            ) : (
                                <strong>{shipName || "--"}</strong>
                            )}
                        </div>
                        <label className={`zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__time${validationRequested && !endTime.trim() ? " zone-reservation-weigh-dialog__field--invalid" : ""}`}>
                            <span>
                                <em className="zone-reservation-weigh-dialog__required-mark">*</em>装船结束时间
                            </span>
                            <Input
                                prefix={
                                    <svg
                                        aria-hidden="true"
                                        className="zone-reservation-weigh-dialog__time-icon"
                                        viewBox="0 0 1024 1024"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M512 896a384 384 0 1 0 0-768 384 384 0 0 0 0 768m0 64a448 448 0 1 1 0-896 448 448 0 0 1 0 896"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M480 256a32 32 0 0 1 32 32v256a32 32 0 0 1-64 0V288a32 32 0 0 1 32-32"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M480 512h256q32 0 32 32t-32 32H480q-32 0-32-32t32-32"
                                        />
                                    </svg>
                                }
                                aria-label="装船结束时间"
                                aria-invalid={validationRequested && !endTime.trim()}
                                placeholder="请选择"
                                value={endTime}
                                onChange={event => setEndTime(event.target.value)}
                            />
                            {validationRequested && !endTime.trim() ? (
                                <span className="zone-reservation-weigh-dialog__field-error" role="alert">请选择装船结束时间</span>
                            ) : null}
                        </label>
                    </div>
                </section>

                <section className="zone-reservation-weigh-dialog__section">
                    <div className="zone-reservation-weigh-dialog__section-title">物料信息</div>
                    {lines.map((line, index) => (
                        <div
                            className="zone-reservation-weigh-dialog__material-card"
                            key={`${line.goodsCode}-${index}`}
                        >
                            <div className="zone-reservation-weigh-dialog__material-grid">
                                <div className="zone-reservation-weigh-dialog__field">
                                    <span>物料名称</span>
                                    <strong>{line.goodsName || "--"}</strong>
                                </div>
                                <div className={`zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short${validationRequested && mode === "edit" && canEditUnitPrice && weighUnitPriceError(priceInputs[index] ?? "") ? " zone-reservation-weigh-dialog__field--invalid" : ""}`}>
                                    <span>
                                        {mode === "edit" ? (
                                            <em className="zone-reservation-weigh-dialog__required-mark">*</em>
                                        ) : null}
                                        单价
                                    </span>
                                    {mode === "edit" && canEditUnitPrice ? (
                                        <Input
                                            aria-label="单价"
                                            aria-invalid={validationRequested && !!weighUnitPriceError(priceInputs[index] ?? "")}
                                            suffix="元"
                                            value={priceInputs[index] ?? ""}
                                            onChange={event => {
                                                const next = event.target.value;
                                                if (
                                                    !/^\d*(?:\.\d{0,6})?$/.test(next) ||
                                                    Number(next) > ZONE_CONTRACT_UNIT_PRICE_MAX
                                                )
                                                    return;
                                                setPriceInputs(current =>
                                                    current.map((value, itemIndex) =>
                                                        itemIndex === index ? next : value
                                                    )
                                                );
                                                setLines(current =>
                                                    current.map((item, itemIndex) =>
                                                        itemIndex === index
                                                            ? { ...item, unitPrice: next ? Number(next) : Number.NaN }
                                                            : item
                                                    )
                                                );
                                            }}
                                        />
                                    ) : (
                                        <strong>{`¥${Number(line.unitPrice || 0).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</strong>
                                    )}
                                    {validationRequested && mode === "edit" && canEditUnitPrice && weighUnitPriceError(priceInputs[index] ?? "") ? (
                                        <span className="zone-reservation-weigh-dialog__field-error" role="alert">
                                            {weighUnitPriceError(priceInputs[index] ?? "")}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>结算率</span>
                                    <strong>{line.settlementRate || "--"}</strong>
                                </div>
                                <label className={`zone-reservation-weigh-dialog__field${(validationRequested || touchedWeightIndexes.includes(index)) && weighTonError(line.grossWeight, line.plannedTonnage) ? " zone-reservation-weigh-dialog__field--invalid" : ""}`}>
                                    <span>
                                        <em className="zone-reservation-weigh-dialog__required-mark">*</em>过磅吨数
                                    </span>
                                    <Input
                                        aria-label={`${line.goodsName}过磅吨数`}
                                        aria-invalid={!!((validationRequested || touchedWeightIndexes.includes(index)) && weighTonError(line.grossWeight, line.plannedTonnage))}
                                        suffix="吨"
                                        placeholder="请输入"
                                        value={line.grossWeight}
                                        onChange={event =>
                                            setLines(current =>
                                                current.map((item, itemIndex) =>
                                                    itemIndex === index
                                                        ? { ...item, grossWeight: event.target.value }
                                                        : item
                                                )
                                            )
                                        }
                                        onBlur={() => setTouchedWeightIndexes(current => current.includes(index) ? current : [...current, index])}
                                    />
                                    {(validationRequested || touchedWeightIndexes.includes(index)) && weighTonError(line.grossWeight, line.plannedTonnage) ? (
                                        <span className="zone-reservation-weigh-dialog__field-error" role="alert">
                                            {weighTonError(line.grossWeight, line.plannedTonnage)}
                                        </span>
                                    ) : null}
                                </label>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>结算吨数</span>
                                    <strong>
                                        {line.grossWeight
                                            ? `${(Number(line.grossWeight) * (Number(line.settlementRate) || 0)).toLocaleString("zh-CN")} 吨`
                                            : ""}
                                    </strong>
                                </div>
                                <div className="zone-reservation-weigh-dialog__field zone-reservation-weigh-dialog__short">
                                    <span>结算金额</span>
                                    <strong>
                                        {Number(line.grossWeight) > 0 && Number.isFinite(line.unitPrice)
                                            ? `¥${(
                                                  Number(line.grossWeight) *
                                                  line.unitPrice *
                                                  Number(line.settlementRate)
                                              ).toLocaleString("zh-CN", {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2
                                              })}`
                                            : ""}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="zone-reservation-weigh-dialog__attachment">
                        <span>附件</span>
                        <div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".jpg,.jpeg,.png,.pdf"
                                multiple
                                hidden
                                onChange={event => {
                                    if (event.target.files) void uploadAttachments(event.target.files);
                                    event.target.value = "";
                                }}
                            />
                            <div
                                className="zone-reservation-weigh-dialog__upload-placeholder"
                                role="button"
                                tabIndex={0}
                                onClick={() => fileInputRef.current?.click()}
                                onKeyDown={event => {
                                    if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click();
                                }}
                                onDragOver={event => event.preventDefault()}
                                onDrop={event => {
                                    event.preventDefault();
                                    void uploadAttachments(event.dataTransfer.files);
                                }}
                            >
                                <i aria-hidden="true" className="iconfont icon-shangchuan" />
                                <span>点击或拖拽上传文件</span>
                            </div>
                            <div className="zone-reservation-weigh-dialog__attachment-tip">
                                支持 jpg、png、pdf，单个文件不超过10MB，最多上传5个
                            </div>
                            {attachmentFiles.length ? (
                                <div className="zone-reservation-weigh-dialog__file-list">
                                    {attachmentFiles.map(file => (
                                        <div className="zone-reservation-weigh-dialog__file-item" key={file.id}>
                                            <span className="zone-reservation-weigh-dialog__file-name">
                                                <i aria-hidden="true" className="iconfont icon-file" />
                                                <span className="zone-reservation-weigh-dialog__file-name-text">
                                                    {file.name}
                                                </span>
                                            </span>
                                            <button
                                                type="button"
                                                aria-label={`删除附件${file.name}`}
                                                className="zone-reservation-weigh-dialog__remove-file"
                                                onClick={() => {
                                                    setAttachmentFiles(current =>
                                                        current.filter(item => item.id !== file.id)
                                                    );
                                                    setUploadedFileIds(current => current.filter(id => id !== file.id));
                                                }}
                                            >
                                                <i aria-hidden="true" className="iconfont icon-close2" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </div>
        </ZhDialog>
    );
}

export default WeighingListEntryDialog;
